import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { searchesAPI } from '../services/api';
import SearchesPanel from '../components/SearchesPanel';
import SearchInterface from '../components/SearchInterface';
import ResultsTable from '../components/ResultsTable';
import ServerStatsPanel from '../components/ServerStatsPanel';
import { buildSearchObjectsFromCsv } from '../utils/searchCsvUtils';
import './Searches.css';

const Searches = () => {
  const { user, logout, isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searches, setSearches] = useState([]);
  const [selectedSearch, setSelectedSearch] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const [filterType, setFilterType] = useState('all'); // scheduled, instant, all
  const [filterStatus, setFilterStatus] = useState('all'); // all, new, running, opened, cleared
  const [filterUser, setFilterUser] = useState('all'); // all or user_id
  const [searchText, setSearchText] = useState(''); // search text filter
  const [hideFilteredItems, setHideFilteredItems] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(200);
  const [totalSearches, setTotalSearches] = useState(0);
  const selectedSearchIdRef = React.useRef(null);
  const selectedSearchStateRef = React.useRef(null);
  const importInputRef = React.useRef(null);

  // Load searches on mount and when filters change
  useEffect(() => {
    setCurrentPage(1); // Reset to page 1 when filters change
    loadSearches(1);
  }, [filterType, filterStatus, filterUser, searchText]);

  // Load searches when page changes
  useEffect(() => {
    loadSearches(currentPage);
  }, [currentPage]);

  // Keep ref in sync so the polling interval can access the current selected search id
  useEffect(() => {
    selectedSearchIdRef.current = selectedSearch?.id ?? null;
    selectedSearchStateRef.current = selectedSearch?.state ?? null;
  }, [selectedSearch]);

  // Poll every 5s while any search is in_progress to keep last_run_at and state current
  useEffect(() => {
    const hasInProgress = searches.some(s => s.state === 'in_progress');
    if (!hasInProgress) return;

    const interval = setInterval(async () => {
      try {
        const params = {
          page: currentPage,
          limit: itemsPerPage
        };
        if (filterStatus !== 'all') params.state = filterStatus;
        if (filterUser !== 'all') params.user_id = filterUser;
        if (filterType !== 'all') params.type = filterType;
        if (searchText && searchText.trim()) params.search_text = searchText.trim();

        const response = await searchesAPI.list(params);
        const refreshed = response.data.data;

        setSearches(refreshed);

        // Sync selectedSearch if it's in the list
        const currentId = selectedSearchIdRef.current;
        if (currentId) {
          const updated = refreshed.find(s => s.id === currentId);
          if (updated) {
            const prevState = selectedSearchStateRef.current;
            setSelectedSearch(updated);
            // Auto-fetch results when selected search finishes
            if (prevState === 'in_progress' && updated.state === 'done') {
              loadResults(updated.id, updated.state);
            }
          }
        }
      } catch (_) {}
    }, 5000);

    return () => clearInterval(interval);
  }, [searches, filterType, filterStatus, filterUser, searchText, currentPage, itemsPerPage]);

  const loadSearches = async (page = currentPage) => {
    try {
      const params = {
        page,
        limit: itemsPerPage
      };
      
      if (filterStatus !== 'all') {
        params.state = filterStatus;
      }
      
      if (filterUser !== 'all') {
        params.user_id = filterUser;
      }

      if (filterType !== 'all') {
        params.type = filterType;
      }

      if (searchText && searchText.trim()) {
        params.search_text = searchText.trim();
      }
      
      const response = await searchesAPI.list(params);
      setTotalSearches(response.data.pagination?.total || 0);
      const fetchedSearches = response.data.data;
      
      setSearches(fetchedSearches);

      // Sync selectedSearch with latest data from server
      const currentId = selectedSearchIdRef.current;
      if (currentId) {
        const updated = fetchedSearches.find(s => s.id === currentId);
        if (updated) setSelectedSearch(updated);
      } else {
        // On initial load, auto-select search from URL param
        const urlSearchId = parseInt(searchParams.get('search'));
        if (urlSearchId) {
          const found = fetchedSearches.find(s => s.id === urlSearchId);
          if (found) {
            setSelectedSearch(found);
            selectedSearchIdRef.current = found.id;
            loadResults(found.id, found.state);
          }
        }
      }
    } catch (error) {
      console.error('Failed to load searches:', error);
    }
  };

  const loadResults = async (searchId, currentState) => {
    setLoading(true);
    try {
      const response = await searchesAPI.getResults(searchId, { limit: 10000 });
      setResults(response.data.results || []);

      // Mark search as 'opened' when results are viewed (only if it was 'done')
      if (currentState === 'done') {
        await searchesAPI.update(searchId, { state: 'opened' });
        await loadSearches();
      }
    } catch (error) {
      console.error('Failed to load results:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSelect = (search) => {
    setSelectedSearch(search);
    setSearchParams({ search: search.id });
    loadResults(search.id, search.state);
  };

  const handleSearchRun = async (searchData) => {
    const { onlySchedule, ...cleanSearchData } = searchData;
    try {
      setLoading(true);
      
      let searchId;
      if (selectedSearch) {
        // Update existing search
        await searchesAPI.update(selectedSearch.id, cleanSearchData);
        if (!onlySchedule) await searchesAPI.run(selectedSearch.id);
        searchId = selectedSearch.id;
      } else {
        // Create new search and set it as the active search immediately
        const response = await searchesAPI.create(cleanSearchData);
        const createdSearch = response.data.data;
        if (!onlySchedule) await searchesAPI.run(createdSearch.id);
        searchId = createdSearch.id;
        setSelectedSearch(createdSearch);
        setSearchParams({ search: createdSearch.id });
        selectedSearchIdRef.current = createdSearch.id;
      }
      
      // Reload searches list (also syncs selectedSearch via loadSearches)
      await loadSearches();
      
      setLoading(false);
    } catch (error) {
      console.error('Failed to run search:', error);
      setLoading(false);
      alert('Failed to run search: ' + (error.response?.data?.error || error.response?.data?.message || error.message));
    }
  };

  const handleCsvFileSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = null;

    if (!file) {
      return;
    }

    try {
      setLoading(true);
      const text = await file.text();
      const searches = buildSearchObjectsFromCsv(text);

      if (searches.length === 0) {
        alert('No valid search rows found in CSV.');
        return;
      }

      let createdCount = 0;
      for (const searchData of searches) {
        try {
          await searchesAPI.create(searchData);
          createdCount += 1;
        } catch (innerError) {
          console.error('Failed to create search from CSV row:', innerError, searchData);
        }
      }

      await loadSearches();
      alert(`${createdCount} new searches created`);
    } catch (error) {
      console.error('Failed to import searches from CSV:', error);
      alert('Failed to import searches from CSV: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearchClear = async (searchId) => {
    if (!window.confirm('Are you sure you want to clear all results for this search?')) {
      return;
    }
    
    try {
      await searchesAPI.clear(searchId);
      await loadSearches();
      if (selectedSearch?.id === searchId) {
        setResults([]);
        loadSearches();
      }
    } catch (error) {
      console.error('Failed to clear search:', error);
      alert('Failed to clear search: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleSearchDelete = async (searchId) => {
    if (!window.confirm('Are you sure you want to delete this search?')) {
      return;
    }
    
    try {
      await searchesAPI.delete(searchId);
      if (selectedSearch?.id === searchId) {
        setSelectedSearch(null);
        setResults([]);
      }
      await loadSearches();
    } catch (error) {
      console.error('Failed to delete search:', error);
      alert('Failed to delete search: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleNewSearch = () => {
    setSelectedSearch(null);
    setResults([]);
    setSearchParams({});
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const totalPages = Math.ceil(totalSearches / itemsPerPage);

  const handleTogglePause = async (searchId, currentPaused) => {
    try {
      await searchesAPI.update(searchId, { paused: !currentPaused });
      await loadSearches();
    } catch (error) {
      console.error('Failed to toggle pause:', error);
      alert('Failed to update search: ' + (error.response?.data?.error || error.message));
    }
  };

  const handlePauseAll = async (visibleSearches) => {
    const unpausedSearches = visibleSearches.filter(s => !s.paused);
    if (unpausedSearches.length === 0) return;
    try {
      await Promise.all(unpausedSearches.map(s => searchesAPI.update(s.id, { paused: true })));
      await loadSearches();
    } catch (error) {
      console.error('Failed to pause all searches:', error);
      alert('Failed to pause searches: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteAll = async () => {
    try {
      // Build params with all filters
      const params = {};
      if (filterStatus !== 'all') params.state = filterStatus;
      if (filterUser !== 'all') params.user_id = filterUser;
      if (filterType !== 'all') params.type = filterType;
      if (searchText && searchText.trim()) params.search_text = searchText.trim();

      // Get current count from state (this is the total matching filters)
      const countToDelete = totalSearches;

      if (countToDelete === 0) {
        alert('No searches to delete with current filters');
        return;
      }

      const confirmed = window.confirm(
        `Are you sure you want to permanently delete ${countToDelete} search(es)?\n\nThis will delete all searches matching your current filters, not just the ones on this page.`
      );

      if (!confirmed) return;

      await searchesAPI.bulkDelete(params);
      
      // Clear selected search if it was deleted
      setSelectedSearch(null);
      setResults([]);
      
      // Reload searches
      setCurrentPage(1);
      await loadSearches(1);
      
      alert(`Successfully deleted ${countToDelete} search(es)`);
    } catch (error) {
      console.error('Failed to delete searches:', error);
      alert('Failed to delete searches: ' + (error.response?.data?.error || error.message));
    }
  };

  const downloadBlobFile = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const parseFilenameFromContentDisposition = (contentDispositionHeader) => {
    if (!contentDispositionHeader) return null;
    const match = contentDispositionHeader.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
    const encodedFilename = match?.[1];
    const plainFilename = match?.[2];
    if (encodedFilename) {
      try {
        return decodeURIComponent(encodedFilename);
      } catch (_) {
        return encodedFilename;
      }
    }
    return plainFilename || null;
  };

  const extractBlobErrorMessage = async (blob) => {
    if (!(blob instanceof Blob)) return null;
    try {
      const text = await blob.text();
      if (!text) return null;
      try {
        const parsed = JSON.parse(text);
        return parsed?.error || parsed?.message || null;
      } catch (_) {
        return text;
      }
    } catch (_) {
      return null;
    }
  };

  const handleDownloadAllUnopened = async () => {
    setLoading(true);
    try {
      const response = await searchesAPI.downloadDoneGreenResultsCsv();
      const filename = parseFilenameFromContentDisposition(response.headers?.['content-disposition'])
        || `searches_done_green_${Date.now()}.csv`;

      downloadBlobFile(response.data, filename);
      await loadSearches();
    } catch (error) {
      console.error('Failed to download unopened searches:', error);
      const backendError = await extractBlobErrorMessage(error.response?.data);
      alert('Failed to download unopened searches: ' + (backendError || error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Filter results based on hideFilteredItems
  const displayResults = hideFilteredItems
    ? results.filter(r => !r.filtered_out)
    : results;

  return (
    <div className="searches-page">
      <header className="searches-header">
        <h1>Product Sourcing</h1>
        <div className="searches-header-actions">
          <button className="btn-new-search" onClick={handleNewSearch}>
            + Start New Search
          </button>
          <button
            className="btn-new-search ms-2"
            onClick={() => importInputRef.current?.click()}
            disabled={loading}
          >
            Import Searches from CSV
          </button>
          <input
            ref={importInputRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={handleCsvFileSelected}
          />
        </div>
        <div className="user-info">
          <span className="user-name">
            {user?.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : user?.email}
          </span>
          {isAdmin() && <span className="badge badge-info ms-2">Admin</span>}
          <button onClick={logout} className="btn btn-outline-danger ms-2">
            Logout
          </button>
        </div>
      </header>

      <div className="searches-container">
        <SearchesPanel
          searches={searches}
          selectedSearch={selectedSearch}
          onSearchSelect={handleSearchSelect}
          onSearchDelete={handleSearchDelete}
          onSearchClear={handleSearchClear}
          onNewSearch={handleNewSearch}
          collapsed={panelCollapsed}
          onToggleCollapse={() => setPanelCollapsed(!panelCollapsed)}
          filterType={filterType}
          onFilterTypeChange={setFilterType}
          filterStatus={filterStatus}
          onFilterStatusChange={setFilterStatus}
          filterUser={filterUser}
          onFilterUserChange={setFilterUser}
          isAdmin={isAdmin()}
          onRefresh={loadSearches}
          onTogglePause={handleTogglePause}
          onPauseAll={handlePauseAll}
          onDeleteAll={handleDeleteAll}
          onDownloadAllUnopened={handleDownloadAllUnopened}
          searchText={searchText}
          onSearchTextChange={setSearchText}
          currentPage={currentPage}
          totalPages={totalPages}
          totalSearches={totalSearches}
          onPageChange={handlePageChange}
        />

        <div className="searches-main">
          <SearchInterface
            search={selectedSearch}
            onRun={handleSearchRun}
            loading={loading}
            hideFilteredItems={hideFilteredItems}
            onToggleHideFiltered={() => setHideFilteredItems(!hideFilteredItems)}
          />

          <ResultsTable
            results={displayResults}
            loading={loading}
            searchState={selectedSearch?.state}
            statusMessage={selectedSearch?.status_message}
            onRefresh={() => selectedSearch && loadResults(selectedSearch.id, selectedSearch.state)}
          />
        </div>

        <ServerStatsPanel />
      </div>
    </div>
  );
};

export default Searches;
